import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Trash2, AlertTriangle, ListTodo, Calendar, UserCircle2, Pause, Play, Check, X } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask } from '@/hooks/useTasks';
import {
  TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TASK_STATUS_COLORS,
  type Task, type TaskPriority, type TaskStatus,
} from '@/types';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  basse:   'var(--tx-3)',
  moyenne: 'var(--p)',
  haute:   'var(--danger)',
};

const STATUS_ICONS: Record<TaskStatus, typeof Pause> = {
  en_attente: Pause,
  en_cours:   Play,
  terminee:   Check,
  ajournee:   X,
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

const isOverdue = (iso: string | null, status: TaskStatus) =>
  status !== 'terminee' && status !== 'ajournee' && iso && new Date(iso).getTime() < new Date().setHours(0, 0, 0, 0);

/**
 * Composant "Mon planning du jour" sur la HomePage.
 * Bouton "Ajouter" → dialog modal (UX cohérente avec le reste de l'app).
 * 4 statuts cliquables par tâche : En attente · En cours · Terminée · Ajournée.
 * Auto-refresh 30s pour suivi temps réel (utile pour l'attributeur).
 */
export const MyTaskList = () => {
  const { data: tasks = [], isLoading } = useTasks();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const open    = tasks.filter((t) => t.status === 'en_attente' || t.status === 'en_cours');
  const closed  = tasks.filter((t) => t.status === 'terminee' || t.status === 'ajournee');

  const setStatus = (t: Task, status: TaskStatus) => {
    if (t.status === status) return;
    update.mutate({ id: t.id, payload: { status } });
  };

  const onRemove = async (t: Task) => {
    if (!(await demander.confirmer({ titre: `Supprimer « ${t.title} » ?`, confirmer: 'Supprimer', danger: true }))) return;
    remove.mutate(t.id);
  };

  return (
    <>
      <div className="ds-bp">
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ListTodo size={16} style={{ color: 'var(--p)' }} />
              Mon planning du jour
            </div>
            <div className="ds-bp-s">{open.length} à faire · {closed.length} clôturée{closed.length > 1 ? 's' : ''}</div>
          </div>
          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="ds-bp-b" style={{ padding: 14 }}>
          {error && (
            <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
              <span className="ds-alert-ico"><AlertTriangle size={14} /></span>
              <div className="ds-alert-txt">{error}</div>
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--tx-3)' }}>Chargement…</div>
          ) : tasks.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <ListTodo size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
              <p style={{ fontSize: 13, color: 'var(--tx-3)', marginBottom: 12 }}>Aucune tâche pour le moment</p>
              <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setDialogOpen(true)}>
                <Plus size={12} /> Ajouter ma première tâche
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[...open, ...closed].map((t) => {
                const overdue = isOverdue(t.dueDate, t.status);
                const isClosed = t.status === 'terminee' || t.status === 'ajournee';
                return (
                  <div
                    key={t.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 'var(--r-sm)',
                      background: isClosed ? 'var(--bg-deep)' : 'var(--surface)',
                      border: `1px solid ${overdue ? 'var(--danger)' : 'var(--bdr-2)'}`,
                      opacity: isClosed ? 0.7 : 1,
                      transition: 'all .15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13, fontWeight: 500,
                            color: isClosed ? 'var(--tx-3)' : 'var(--tx)',
                            textDecoration: t.status === 'terminee' ? 'line-through' : 'none',
                            fontStyle: t.status === 'ajournee' ? 'italic' : 'normal',
                          }}
                        >{t.title}</div>
                        {(t.dueDate || t.priority !== 'moyenne' || t.assignedBy) && (
                          <div style={{ display: 'flex', gap: 10, marginTop: 3, fontSize: 11, color: overdue ? 'var(--danger)' : 'var(--tx-3)', flexWrap: 'wrap' }}>
                            {t.assignedBy && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--p)', fontWeight: 600 }}>
                                <UserCircle2 size={11} /> Attribuée par {t.assignedBy.fullName}
                              </span>
                            )}
                            {t.dueDate && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <Calendar size={11} /> {fmtDate(t.dueDate)} {overdue && '⚠ en retard'}
                              </span>
                            )}
                            {t.priority !== 'moyenne' && (
                              <span style={{ color: PRIORITY_COLOR[t.priority], fontWeight: 600 }}>
                                {TASK_PRIORITY_LABELS[t.priority]}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => onRemove(t)}
                        className="ds-row-btn"
                        title="Supprimer"
                        disabled={remove.isPending}
                        style={{ flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {/* 4 boutons statut */}
                    <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                      {(Object.keys(TASK_STATUS_LABELS) as TaskStatus[]).map((s) => {
                        const Icon = STATUS_ICONS[s];
                        const active = t.status === s;
                        const color = TASK_STATUS_COLORS[s];
                        return (
                          <button
                            key={s}
                            onClick={() => setStatus(t, s)}
                            disabled={update.isPending}
                            style={{
                              flex: 1,
                              padding: '4px 6px',
                              fontSize: 10.5,
                              fontWeight: active ? 700 : 500,
                              color: active ? '#fff' : color,
                              background: active ? color : 'transparent',
                              border: `1px solid ${active ? color : 'var(--bdr)'}`,
                              borderRadius: 4,
                              cursor: update.isPending ? 'wait' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                              transition: 'all .12s',
                            }}
                            title={TASK_STATUS_LABELS[s]}
                          >
                            <Icon size={11} strokeWidth={active ? 3 : 2} />
                            {TASK_STATUS_LABELS[s]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <AddTaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onError={setError} />
    </>
  );
};

// ── Dialog modal "Ajouter une tâche" ───────────────────────────────────────

const AddTaskDialog = ({ open, onClose, onError }: { open: boolean; onClose: () => void; onError: (m: string | null) => void }) => {
  const create = useCreateTask();
  const [title, setTitle]         = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate]     = useState('');
  const [priority, setPriority]   = useState<TaskPriority>('moyenne');
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(''); setDescription(''); setDueDate(''); setPriority('moyenne'); setError(null);
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Veuillez saisir un titre');
      return;
    }
    setError(null);
    onError(null);
    try {
      await create.mutateAsync({
        title:       title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate:     dueDate || undefined,
      });
      onClose();
    } catch (err) {
      const msg = extractError(err).message;
      setError(msg);
      onError(msg);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter une tâche" subtitle="Crée une tâche dans votre planning personnel" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Field label="Titre *">
          <input
            value={title} onChange={(e) => setTitle(e.target.value)}
            required autoFocus
            className="ds-fi" style={{ width: '100%' }}
            placeholder="Ex : Préparer la réunion équipe vendredi"
          />
        </Field>

        <Field label="Description (optionnel)">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="Détails, contexte, sous-tâches…" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Échéance">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Priorité">
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={create.isPending}>
            <Plus size={14} /> {create.isPending ? 'Ajout…' : 'Ajouter la tâche'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
